import { useEffect, useMemo, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { SiteHeader } from "@/components/site-header"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Pagination } from "@/components/ui/pagination"
import { api, authorizedFetch } from "@/lib/api"
import { usePermissions } from "@/hooks/use-permissions"
import { PencilIcon, PlusIcon, SearchIcon, Trash2Icon } from "lucide-react"
import { RestoreIcon } from "@/components/icons/RestoreIcon"

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:3001"
const PAGE_SIZE = 25

interface PayConceptRow {
  id: string
  code: string
  name: string
  currencyId: string
  currencyCode: string
  unitRate: string
  deletedAt: string | null
  createdAt: string
  updatedAt: string
}

interface Currency {
  id: string
  code: string
  name: string
}

const EMPTY_FORM = {
  name: "",
  code: "",
  currencyId: "",
  unitRate: "0",
}

type FilterStatus = "active" | "deleted" | "all"
type FormState = typeof EMPTY_FORM

export default function PayConceptsPage() {
  const qc = useQueryClient()
  const { can } = usePermissions()

  const [search, setSearch] = useState("")
  const [filterStatus, setFilterStatus] = useState<FilterStatus>("active")
  const [page, setPage] = useState(1)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [editing, setEditing] = useState<PayConceptRow | null>(null)
  const [deleting, setDeleting] = useState<PayConceptRow | null>(null)
  const [form, setForm] = useState<FormState>(({ ...EMPTY_FORM }))
  const [formError, setFormError] = useState<string | null>(null)

  // ── Queries ───────────────────────────────────────────────────────────────
  const { data: payConcepts = [], isLoading } = useQuery({
    queryKey: ["pay-concepts", filterStatus],
    queryFn: async () => {
      const query: Record<string, string> = {}
      if (filterStatus === "active") query.deleted = "false"
      else if (filterStatus === "deleted") query.deleted = "true"

      const res = await api["pay-concepts"].$get({ query })
      if (!res.ok) throw new Error("Error loading pay concepts")
      return res.json() as Promise<PayConceptRow[]>
    },
  })

  const { data: currencies = [] } = useQuery({
    queryKey: ["currencies", "active"],
    queryFn: async () => {
      const res = await api.currencies.$get({ query: { deleted: "false" } })
      if (!res.ok) return []
      return res.json() as Promise<Currency[]>
    },
    staleTime: 60000,
  })

  // ── Derived data ─────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const term = search.toLowerCase().trim()
    return payConcepts.filter((item) => {
      if (!term) return true
      return (
        item.name.toLowerCase().includes(term) ||
        item.code.toLowerCase().includes(term)
      )
    })
  }, [payConcepts, search])

  useEffect(() => { setPage(1) }, [search, filterStatus])

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  // ── Mutations ─────────────────────────────────────────────────────────────
  const createMut = useMutation({
    mutationFn: async (data: FormState) => {
      const res = await api["pay-concepts"].$post({
        json: {
          name: data.name,
          code: data.code,
          currencyId: data.currencyId,
          unitRate: data.unitRate,
        },
      })
      if (!res.ok) {
        const body = await res.json() as { error?: string }
        throw new Error(body.error ?? "Error al crear concepto de pago")
      }
      return res.json()
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pay-concepts"] })
      closeSheet()
    },
    onError: (err: Error) => setFormError(err.message),
  })

  const updateMut = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: FormState }) => {
      const res = await api["pay-concepts"][":id"].$patch({
        param: { id },
        json: {
          name: data.name,
          code: data.code,
          currencyId: data.currencyId,
          unitRate: data.unitRate,
        },
      })
      if (!res.ok) {
        const body = await res.json() as { error?: string }
        throw new Error(body.error ?? "Error al actualizar concepto de pago")
      }
      return res.json()
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pay-concepts"] })
      closeSheet()
    },
    onError: (err: Error) => setFormError(err.message),
  })

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      const res = await api["pay-concepts"][":id"].$delete({ param: { id } })
      if (!res.ok) {
        const body = await res.json() as { error?: string }
        throw new Error(body.error ?? "Error al eliminar concepto de pago")
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pay-concepts"] })
      setDeleteOpen(false)
      setDeleting(null)
    },
    onError: (err: Error) => setFormError(err.message),
  })

  const restoreMut = useMutation({
    mutationFn: async (id: string) => {
      await authorizedFetch(`${API_URL}/api/pay-concepts/${id}/restore`, {
        method: "PATCH",
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pay-concepts"] })
    },
    onError: (err: Error) => setFormError(err.message),
  })

  // ── Helpers ───────────────────────────────────────────────────────────────
  function closeSheet() {
    setSheetOpen(false)
    setEditing(null)
    setForm(({ ...EMPTY_FORM }))
    setFormError(null)
  }

  function openCreate() {
    setEditing(null)
    setForm(({ ...EMPTY_FORM }))
    setFormError(null)
    setSheetOpen(true)
  }

  function openEdit(item: PayConceptRow) {
    setEditing(item)
    setForm({
      name: item.name,
      code: item.code,
      currencyId: item.currencyId,
      unitRate: Number(item.unitRate).toFixed(2),
    })
    setFormError(null)
    setSheetOpen(true)
  }

  function openDelete(item: PayConceptRow) {
    setDeleting(item)
    setDeleteOpen(true)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setFormError(null)

    const payload: FormState = {
      name: form.name.trim(),
      code: form.code.trim(),
      currencyId: form.currencyId,
      unitRate: form.unitRate,
    }

    if (!payload.name || !payload.code || !payload.currencyId) {
      setFormError("Completá todos los campos requeridos")
      return
    }

    if (editing) updateMut.mutate({ id: editing.id, data: payload })
    else createMut.mutate(payload)
  }

  const isSaving = createMut.isPending || updateMut.isPending

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <SiteHeader title="Conceptos de pago" />
      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[220px]">
            <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por código o nombre..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
            />
          </div>

          <Select value={filterStatus} onValueChange={(v) => setFilterStatus(v as FilterStatus)}>
            <SelectTrigger className="w-40" size="sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Activos</SelectItem>
              <SelectItem value="deleted">Eliminados</SelectItem>
              <SelectItem value="all">Todos</SelectItem>
            </SelectContent>
          </Select>

          {can("write", "config") && (
            <Button onClick={openCreate} className="gap-2">
              <PlusIcon className="size-4" /> Nuevo concepto
            </Button>
          )}
        </div>

        <div className="rounded-lg border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-32">Código</TableHead>
                <TableHead>Nombre</TableHead>
                <TableHead className="w-32">Moneda</TableHead>
                <TableHead className="w-32 text-right">Tarifa Unitaria</TableHead>
                {can("write", "config") && <TableHead className="w-32 text-right pr-4">Acciones</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={can("write", "config") ? 5 : 4} className="text-center py-16 text-muted-foreground">
                    Cargando conceptos de pago...
                  </TableCell>
                </TableRow>
              ) : paged.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={can("write", "config") ? 5 : 4} className="text-center py-16 text-muted-foreground">
                    {search ? "No hay resultados para la búsqueda" : "No se encontraron conceptos de pago"}
                  </TableCell>
                </TableRow>
              ) : (
                paged.map((item) => (
                  <TableRow key={item.id} className={item.deletedAt ? "opacity-70" : undefined}>
                    <TableCell>
                      <span className="font-mono text-xs bg-muted px-2 py-0.5 rounded tracking-wide">
                        {item.code}
                      </span>
                    </TableCell>
                    <TableCell className="font-medium text-sm">{item.name}</TableCell>
                    <TableCell>
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full border bg-background">
                        {item.currencyCode}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {Number(item.unitRate).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
                    </TableCell>
                    {can("write", "config") && (
                      <TableCell className="text-right pr-4">
                        <div className="flex justify-end gap-1">
                          {item.deletedAt ? (
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              onClick={() => restoreMut.mutate(item.id)}
                              disabled={restoreMut.isPending}
                              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              title="Restaurar"
                            >
                              <RestoreIcon className="size-3.5" />
                            </Button>
                          ) : (
                            <>
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                onClick={() => openEdit(item)}
                                title="Editar"
                              >
                                <PencilIcon className="size-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                onClick={() => openDelete(item)}
                                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                title="Eliminar"
                              >
                                <Trash2Icon className="size-3.5" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={filtered.length}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
          />
        </div>
      </div>

      {/* ── Sheet create / edit ─────────────────────────────────────────────── */}
      <Sheet open={sheetOpen} onOpenChange={(open) => { if (!open) closeSheet(); else setSheetOpen(true) }}>
        <SheetContent side="right" className="sm:max-w-sm overflow-y-auto flex flex-col">
          <SheetHeader>
            <SheetTitle>{editing ? "Editar concepto" : "Nuevo concepto"}</SheetTitle>
            <SheetDescription>Configurá la tarifa por unidad para este concepto.</SheetDescription>
          </SheetHeader>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4 px-4 pb-4 flex-1">
            <div className="grid gap-1.5">
              <Label htmlFor="pc-code">Código</Label>
              <Input
                id="pc-code"
                value={form.code}
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                maxLength={10}
                className="font-mono uppercase"
                placeholder="KM"
                required
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="pc-name">Nombre</Label>
              <Input
                id="pc-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                maxLength={250}
                placeholder="Kilómetros Pagos"
                required
              />
            </div>
            <div className="grid gap-1.5">
              <Label>Moneda</Label>
              <Select
                value={form.currencyId}
                onValueChange={(v) => setForm((f) => ({ ...f, currencyId: v ?? "" }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccioná moneda">
                    {currencies.find((c) => c.id === form.currencyId)
                      ? `${currencies.find((c) => c.id === form.currencyId)!.code} — ${currencies.find((c) => c.id === form.currencyId)!.name}`
                      : null}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {currencies.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.code} — {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="pc-rate">Tarifa Unitaria</Label>
              <Input
                id="pc-rate"
                type="number"
                step="0.0001"
                value={form.unitRate}
                onChange={(e) => setForm((f) => ({ ...f, unitRate: e.target.value }))}
                placeholder="0.0000"
                required
              />
            </div>

            {formError && (
              <p className="text-sm text-destructive bg-destructive/10 rounded px-3 py-2">{formError}</p>
            )}

            <SheetFooter className="px-0 mt-auto pt-4">
              <Button type="button" variant="outline" onClick={closeSheet}>Cancelar</Button>
              <Button type="submit" disabled={isSaving}>
                {isSaving ? "Guardando..." : editing ? "Guardar cambios" : "Crear concepto"}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      {/* ── Dialog delete ───────────────────────────────────────────────────── */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Eliminar concepto de pago</DialogTitle>
            <DialogDescription>
              ¿Eliminás el concepto <strong>{deleting?.code}</strong> — {deleting?.name}? Esta acción es reversible desde la opción de restaurar.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose>
              <Button variant="outline" onClick={() => setDeleting(null)}>Cancelar</Button>
            </DialogClose>
            <Button
              variant="destructive"
              disabled={deleteMut.isPending}
              onClick={() => deleting && deleteMut.mutate(deleting.id)}
            >
              {deleteMut.isPending ? "Eliminando..." : "Eliminar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
